/**
 * AI-ENRICH.1 — Lese-Pfad für die KI-recherchierten Daten.
 */

import { logCaughtError } from './error-log';
import { supabase } from './supabase';

export interface AIResearchSource {
  url: string;
  title: string;
  published?: string;
}

export interface AIResearchData {
  symbol: string;
  // News
  news_sentiment_score: number | null;
  news_sentiment_summary: string | null;
  news_sources: AIResearchSource[] | null;
  news_updated_at: string | null;
  // Analysten
  analyst_buy_count: number | null;
  analyst_hold_count: number | null;
  analyst_sell_count: number | null;
  analyst_target_price: number | null;
  analyst_summary: string | null;
  analyst_sources: AIResearchSource[] | null;
  analyst_updated_at: string | null;
  // Insider
  insider_score: number | null;
  insider_summary: string | null;
  insider_sources: AIResearchSource[] | null;
  insider_updated_at: string | null;
  // Burggraben
  moat_score: number | null;
  moat_assessment: string | null;
  moat_sources: AIResearchSource[] | null;
  moat_updated_at: string | null;
  // PPLX.1 — Fundamentals-Gap-Fill (KI-recherchierte Werte)
  ai_pe_ttm: number | null;
  ai_price_to_book: number | null;
  ai_price_to_sales: number | null;
  ai_roa_ttm_pct: number | null;
  ai_roe_ttm_pct: number | null;
  ai_net_profit_margin_pct: number | null;
  ai_operating_margin_pct: number | null;
  ai_debt_to_equity: number | null;
  ai_current_ratio: number | null;
  ai_revenue_growth_5y_pct: number | null;
  ai_eps_growth_5y_pct: number | null;
  ai_dividend_yield_pct: number | null;
  ai_beta: number | null;
  // V6-DATA.5: Erweiterte Pflichtfelder
  ai_fcf_ttm_usd: number | null;
  ai_fcf_margin_pct: number | null;
  ai_roic_ttm_pct: number | null;
  ai_payout_ratio_pct: number | null;
  ai_dividend_growth_5y_pct: number | null;
  // V6-DATA.7: Score-Block-Lücken
  ai_market_cap_usd_b: number | null;
  ai_high_52w_usd: number | null;
  fundamentals_summary: string | null;
  fundamentals_sources: AIResearchSource[] | null;
  fundamentals_updated_at: string | null;
  // Earnings-Reaktion
  earnings_last_date: string | null;
  earnings_reaction_pct: number | null;
  earnings_reaction_summary: string | null;
  earnings_reaction_sources: AIResearchSource[] | null;
  earnings_reaction_updated_at: string | null;
  // Dividenden-Track-Record
  dividend_years_increased: number | null;
  dividend_last_cut_year: number | null;
  dividend_summary: string | null;
  dividend_sources: AIResearchSource[] | null;
  dividend_updated_at: string | null;
  // V7.L2: Makro + Politik pro Symbol
  macro_impact_summary: string | null;
  political_risk_factors: Array<{
    factor: string;
    impact_level: 'hoch' | 'mittel' | 'niedrig';
    explanation: string;
  }> | null;
  macro_updated_at: string | null;
  // Welle 13 — Finnhub-Free-Tier (News + Analyst-Konsens, kein Abo nötig)
  fh_analyst_strong_buy_count: number | null;
  fh_analyst_buy_count: number | null;
  fh_analyst_hold_count: number | null;
  fh_analyst_sell_count: number | null;
  fh_analyst_strong_sell_count: number | null;
  fh_analyst_period: string | null;
  fh_price_target_mean: number | null;
  fh_price_target_high: number | null;
  fh_price_target_low: number | null;
  fh_price_target_updated_at: string | null;
  fh_news_count_7d: number | null;
  fh_news_top_headline: string | null;
  fh_news_top_headline_url: string | null;
  fh_news_top_headline_at: string | null;
  fh_news_sentiment_bullish_pct: number | null;
  fh_news_sentiment_bearish_pct: number | null;
  fh_news_sentiment_score: number | null;
  fh_news_buzz_score: number | null;
  // W74.8 — fh_social_* entfernt: Finnhub-Premium-only-Endpoint liefert 403 (Free-
  // Tier). Spalten + Schreib-Logik im Backend bereits ungenutzt. Frontend-Reads
  // weiterhin tolerant via optional-chaining falls noch alte DB-Rows existieren.
  fh_fetched_at: string | null;
  fh_last_error: string | null;
  // Welle 13 — Fund-Trends / Smart-Money (Perplexity + SEC EDGAR)
  ai_fund_holdings_summary: string | null;
  ai_fund_net_buying_pct: number | null;
  ai_fund_holdings_fetched_at: string | null;
  // Welle 13.5 — AI-Analyst-Kursziele (Ersatz für Finnhub-Premium)
  ai_price_target_mean: number | null;
  ai_price_target_high: number | null;
  ai_price_target_low: number | null;
  ai_price_target_analyst_count: number | null;
  ai_price_target_source_summary: string | null;
  ai_price_target_updated_at: string | null;
  // Welle 13.5 — AI-Sentiment-Anteile
  ai_news_bullish_pct: number | null;
  ai_news_bearish_pct: number | null;
  ai_news_neutral_pct: number | null;
  // Meta
  last_run_at: string | null;
  last_error: string | null;
}

/**
 * Merge-Helfer: Liefert für eine Fundamentals-Kennzahl entweder den Provider-Wert
 * oder den AI-recherchierten Fallback. Der Provider-Wert hat immer Vorrang.
 */
export function mergeFundamental<T>(
  providerValue: T | null | undefined,
  aiValue: T | null | undefined
): { value: T | null; source: 'provider' | 'ai' | 'none' } {
  if (providerValue !== null && providerValue !== undefined) {
    return { value: providerValue as T, source: 'provider' };
  }
  if (aiValue !== null && aiValue !== undefined) {
    return { value: aiValue as T, source: 'ai' };
  }
  return { value: null, source: 'none' };
}

export async function fetchAIResearch(
  symbol: string,
  signal?: AbortSignal
): Promise<AIResearchData | null> {
  if (signal?.aborted) return null;
  const { data, error } = await supabase
    .from('v5_ai_research_cache')
    .select('*')
    .eq('symbol', symbol.toUpperCase())
    .maybeSingle();
  if (error) {
    // Audit-Welle A: Fehler in v5_error_log statt nur console.warn
    logCaughtError('ai-research.fetchAIResearch', error, {
      symbol,
      severity: 'warn',
    });
    return null;
  }
  return (data ?? null) as AIResearchData | null;
}

/**
 * Batch-Lookup für die Watchlist-Page: Liefert eine Map symbol → AIResearchData
 * für alle übergebenen Symbole in einer einzigen Query. Wird auf der Watchlist
 * + im Dashboard genutzt, damit nicht 50 Single-Fetches pro Render entstehen.
 */
export async function fetchAIResearchMany(
  symbols: string[],
  signal?: AbortSignal
): Promise<Map<string, AIResearchData>> {
  if (signal?.aborted || symbols.length === 0) return new Map();
  const upper = symbols.map((s) => s.toUpperCase());
  const { data, error } = await supabase
    .from('v5_ai_research_cache')
    .select('*')
    .in('symbol', upper);
  if (error) {
    logCaughtError('ai-research.fetchAIResearchMany', error, {
      severity: 'warn',
      context: { symbol_count: symbols.length },
    });
    return new Map();
  }
  const map = new Map<string, AIResearchData>();
  for (const row of (data ?? []) as AIResearchData[]) {
    map.set(row.symbol.toUpperCase(), row);
  }
  return map;
}

/** Alter der Daten in Tagen (relative für UI). */
export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * Welle 13 — News-Spikes (Top-N Symbole mit höchster News-Aktivität 7T).
 * Liest direkt aus v5_ai_research_cache und filtert auf Symbole mit
 * `fh_news_count_7d >= minCount`. Wird im Dashboard "Heute wichtig" genutzt.
 */
export interface NewsSpikeEntry {
  symbol: string;
  fh_news_count_7d: number;
  fh_news_top_headline: string | null;
  fh_news_top_headline_url: string | null;
  fh_news_top_headline_at: string | null;
  fh_news_buzz_score: number | null;
  fh_news_sentiment_bullish_pct: number | null;
  fh_news_sentiment_bearish_pct: number | null;
}

export async function fetchTopNewsSpikes(
  options: { minCount?: number; limit?: number } = {},
  signal?: AbortSignal
): Promise<NewsSpikeEntry[]> {
  if (signal?.aborted) return [];
  const minCount = options.minCount ?? 5;
  const limit = options.limit ?? 5;
  const { data, error } = await supabase
    .from('v5_ai_research_cache')
    .select(
      'symbol,fh_news_count_7d,fh_news_top_headline,fh_news_top_headline_url,fh_news_top_headline_at,fh_news_buzz_score,fh_news_sentiment_bullish_pct,fh_news_sentiment_bearish_pct'
    )
    .gte('fh_news_count_7d', minCount)
    .order('fh_news_count_7d', { ascending: false })
    .limit(limit);
  if (error) {
    logCaughtError('ai-research.fetchTopNewsSpikes', error, {
      severity: 'warn',
      context: { minCount, limit },
    });
    return [];
  }
  return (data ?? []) as NewsSpikeEntry[];
}
